import { BodyLarge } from '@casavo/base-ui'
import useTranslation from 'next-translate/useTranslation'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { useSessionStorage } from 'usehooks-ts'

import { UserAuthentication } from '@/components/auth/UserAuthentication'
import { ConceptImage } from '@/components/ConceptImage'
import LoginModalForm, { loginModalFormOptions } from '@/components/Modal/Types/LoginModalForm'
import { useAuthentication } from '@/context/AuthProvider'
import { useBottomSheet } from '@/context/BottomSheetContext'
import { useDevice } from '@/context/Device'
import { useModal } from '@/context/ModalContext'
import { useSuggestedCriteria } from '@/context/SuggestedCriteriaProvider'
import { ToastError } from '@/design-lib/components/Toast/ToastError'
import { suggestedFeedPath, suggestedOnboardingPath } from '@/i18n-routes'
import glugluApi, { SuggestedCriteria } from '@/lib/api-gluglu-auth'
import { deferredRequest } from '@/utils/deferredRequest'
import { isStorageSupported } from '@/utils/isStorageSupported'
import { buildAbsoluteLocalizedLink, buildRelativeLocalizedLink } from '@/utils/navigation'
import { GTMEventCategory } from '@/utils/tracking/eventCategory'
import { handleClick, handleShow } from '@/utils/tracking/eventsHandlers'

import { loaderSpinner, loaderWrapper } from './styles.css'
import { Wizard, WizardValues } from './Wizard/wizard'

type Props = {
  currentCity: string
}

const parseWizardSuggestionCriteria = (values: WizardValues): SuggestedCriteria => {
  return {
    budget: values.maxBudget,
    geo_slugs: values.preferredZones.map((zone) => zone.slug),
    rooms_min: parseInt(values.roomsMin, 10),
  }
}

export const Onboarding: React.FC<Props> = ({ currentCity }) => {
  const [triggerSubmit, setTriggerSubmit] = useState(false)
  const [isSubmitting, setSubmitting] = useState(false)
  const { closeBottomSheet, openBottomSheet } = useBottomSheet()
  const [showToastError, setShowToastError] = useState(false)
  const router = useRouter()
  const device = useDevice()
  const { lang, t } = useTranslation()
  const { closeModal, openModal } = useModal()
  const { enableSuggestedCriteria } = useSuggestedCriteria()
  const { isLoading: isLoadingUser, user } = useAuthentication()
  const [sessionCriteria, setSessionCriteria] = useSessionStorage<null | WizardValues>('CASAVO@CRITERIA', null)

  const userAuthenticationTouchPoint = 'FromSuggested'

  useEffect(() => {
    if (user && !isLoadingUser && sessionCriteria !== null) {
      if (user.isLoggedBySocial) {
        saveCriteria(sessionCriteria, true)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isLoadingUser, sessionCriteria])

  useEffect(() => {
    if (user && !isLoadingUser && sessionCriteria !== null && triggerSubmit) {
      if (!user.isLoggedBySocial) {
        saveCriteria(sessionCriteria, true)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isLoadingUser, sessionCriteria, triggerSubmit])

  const handleHideToastError = () => {
    setShowToastError(false)
    setSubmitting(false)
  }

  const handleSubmit = async (values: WizardValues) => {
    handleClick(GTMEventCategory.MainSearchFlow, 'Budget_submit')

    if (user && !isLoadingUser) {
      saveCriteria(values, false)
    }

    if (!user && !isLoadingUser) {
      const onLoggedIn = () => {
        setTriggerSubmit(true)
      }

      setSessionCriteria(values)
      if (device === 'mobile') {
        openBottomSheet(
          <UserAuthentication
            conceptImage={<ConceptImage height={75} name="write" width={75} />}
            mainTitle="Login_Suggested_BottomSheet_Title"
            returnUrl={buildAbsoluteLocalizedLink(`${suggestedOnboardingPath}/${currentCity}`, lang)}
            touchPoint={userAuthenticationTouchPoint}
            onLoggedIn={() => {
              closeBottomSheet()
              onLoggedIn()
            }}
          />,
          { minHeight: 610 }
        )
      } else {
        openModal(
          <LoginModalForm
            conceptImage={<ConceptImage height={75} name="write" width={75} />}
            mainTitle="Login_Suggested_BottomSheet_Title"
            returnURL={buildAbsoluteLocalizedLink(`${suggestedOnboardingPath}/${currentCity}`, lang)}
            touchPoint={userAuthenticationTouchPoint}
            onLoggedIn={() => {
              closeModal()
              onLoggedIn()
            }}
          />,
          loginModalFormOptions
        )
      }
    }
  }

  const saveCriteria = (values: WizardValues, shouldDisplayToastSuccess: boolean) => {
    setSubmitting(true)
    setSessionCriteria(null)
    const parsedValues = parseWizardSuggestionCriteria(values)

    return deferredRequest(glugluApi.addSuggestionCriteria(parsedValues), 1000)
      .then((res) => {
        if (res.ok) {
          handleShow(GTMEventCategory.MainSearchFlow, 'Success')
          enableSuggestedCriteria()
          router.push(buildRelativeLocalizedLink(suggestedFeedPath, lang))
          shouldDisplayToastSuccess &&
            isStorageSupported(() => sessionStorage) &&
            window.sessionStorage.setItem('CASAVO@MAIN_SEARCH', 'search_created')
        }
        if (res.status === 409) {
          glugluApi.updateSuggestedCriteria(parsedValues).then(() => {
            router.push(buildRelativeLocalizedLink(suggestedFeedPath, lang))
            isStorageSupported(() => sessionStorage) &&
              window.sessionStorage.setItem('CASAVO@MAIN_SEARCH', 'search_updated')
          })
        }
      })
      .catch((error) => {
        handleShow(GTMEventCategory.MainSearchFlow, 'Error')
        setShowToastError(true)
        console.error(error.message)
      })
  }
  if (isLoadingUser && !user) return null
  if (isSubmitting) {
    return (
      <>
        <div className={loaderWrapper} data-testid="wizard-loader">
          <div className={loaderSpinner} />
          <BodyLarge color="greyscale500">{t('suggested:Suggested_Loader_1')}</BodyLarge>
        </div>
        <ToastError error={showToastError} setError={handleHideToastError} />
      </>
    )
  }
  return <Wizard handleSubmit={handleSubmit} />
}
